<template>
  <component
    :is="asForm ? BaseForm : 'div'"
    ref="attributeSelectionFormRef"
    class="flex items-end gap-x-2 wrap"
    :form="asForm ? attributeSelection : undefined"
  >
    <section
      v-for="({ groupedOptions, label, type }, i) in variantAttributes.sort((a) => a.type === 'color' ? -1 : 0)"
      :key="type"
      :class="[type === 'color' ? 'w-full' : 'flex-1']"
      :data-scroll-el="`${type}Pickers`"
      :data-test-id="`${type}-groups`"
    >
      <vf-form-error v-if="type !== 'color' && error && !attributeSelection[type]" class="mt-1">
        {{ isDigitalProduct ? $t.selectCardAmountError : replaceAll($t.selectSizeError, { label }) }}
      </vf-form-error>
      <div
        v-for="({ options }, j) in groupedOptions"
        :key="j"
        :data-test-id="`${type}-groups-item`"
      >
        <fieldset v-if="type === 'color'" :aria-labelledby="colorId" class="mb-6 mt-4 <lg:mb-4 <lg:mt-2 <lg:space-y-1">
          <vf-carousel
            class-container="-mx-2 <lg:w-screen <lg:scroll-px-4 <lg:px-4 <lg:-mx-6 lg:wrap"
            class-controls="!hidden"
            @pointerdown.stop
          >
            <vf-color-picker
              v-for="({ available, id, label: optionLabel, price, swatch, url, value }) in
                options.sort((a, b) => a.id === firstSelectedProduct ? -1 : b.id === firstSelectedProduct ? 1 : 0)"
              :key="optionLabel"
              :active="!pending && getIsActive(value, id)"
              :color="swatch?.color"
              :loading="getIsPending(value, id, pending)"
              :name="optionLabel"
              :sale="$feature.showOnSaleSwatchIndicatorOnPDP && price.lowCurrent < price.lowOriginal"
              :size="colorPickerSize"
              :thumbnail="swatch?.image"
              :unavailable="!isComingSoon && (!available && !getIsPending(value, id, pending))"
              :variant="colorPickerShape"
              @click="selectNewColor(enableColorAsAttribute ? value : id, url, optionLabel)"
            />
          </vf-carousel>
          <div class="w-full flex center between">
            <p class="text-xs">
              <span :id="colorId">{{ label }}</span>: {{ attributeSelectionLabels[type]?.value }}
            </p>
            <product-reviews-summary v-if="product?.rating && $feature.showReviewsOnPDP" :product :reviews-data />
          </div>
          <slot />
        </fieldset>
        <fieldset v-else :aria-labelledby="label">
          <legend class="mb-2 body-4" :class="{ 'sr-only': !$feature.pdpShowDropdownLabel }">
            {{ isDigitalProduct ? $t.amount : label }}
            <span v-if="type === 'size' && product?.sizingStandard">
              ({{ product?.sizingStandard }})
            </span>
            <span v-if="type === 'size' && selectedConvertedSizeLabel">
              ({{ selectedConvertedSizeLabel }})
            </span>
          </legend>
          <base-form-field
            v-slot="{ attrs }"
            :name="type"
            :rule="validateRequired()"
            :selector="`${type}Pickers`"
          >
            <div v-if="isDigitalProduct" class="flex gap-2 wrap">
              <vf-size-picker
                v-for="({ available, label: optionLabel, id, value }) in options"
                :key="optionLabel"
                :model-value="attributeSelection[type]"
                :name="label"
                :type
                :unavailable="!getIsPending(value, id, pending) && (!available || !getAttrOptionAvailable(type, value))"
                :value
                v-bind="attrs"
                @input="emit('selectSize', type, value, optionLabel)"
              >
                {{
                  $formatPrice(
                    Number.parseInt(optionLabel),
                    product?.currency,
                    { trailingZeroDisplay: 'stripIfInteger' })
                }}
              </vf-size-picker>
            </div>
            <vf-size-select
              v-else
              v-model="attributeSelection[type]"
              v-bind="attrs"
              class="w-full"
              :show-placeholder-stock-hint="showStockStatusLabel"
              :unavailable="!pending && !options.find(({ value }) => value === attributeSelection[type])?.available"
              @click="handleSizeSelect(variantAttributes[i])"
            >
              {{ attributeSelectionLabels[type]?.value || $t.selectAttribute[type] }}
            </vf-size-select>
          </base-form-field>
        </fieldset>
      </div>
    </section>
    <panel-variant-selection :size="{ sm: 'sm', lg: 'full' }" />
  </component>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { Responsive } from '#types/common'
import type {
  AttributeSelection,
  ColorSwatchShape,
  ProductAttributeSelectionLabels,
  ProductAttributeType,
  ProductExtended,
  SelectedVariant
} from '#types/product'
import type { SizesSubUnion } from '#types/sizes'
import { BaseForm } from '#components'

const {
  colorSize,
  colorSwatch,
  getAttrOptionAvailable = () => true,
  handleNextAttribute,
  hideStockStatusLabel,
  isComingSoon,
  product
} = defineProps<{
  /*
  * variants, productInventory props are added only for consistency with other brands
  * They aren't used in the component
  * but allows to avoid situations with incorrect props resolution when rendering the DOM
  */
  attributeSelectionLabels: ProductAttributeSelectionLabels
  colorSize?: SizesSubUnion<'sm' | 'md' | 'lg'> | Responsive<SizesSubUnion<'sm' | 'md' | 'lg'>>
  colorSwatch?: ColorSwatchShape
  error?: boolean
  getAttrOptionAvailable?: (type: ProductAttributeType, value: string) => boolean | undefined
  getAttrOptionNotifyMe?: (type: ProductAttributeType, value: string) => boolean | undefined
  handleNextAttribute?: () => void
  hideStockStatusLabel?: boolean
  isComingSoon?: boolean
  isDigitalProduct?: boolean
  pending?: boolean
  product: ProductExtended
  selectedVariant?: SelectedVariant
  showBadge?: boolean
  asForm?: boolean
  variants?: any
  reviewsData?: any
}>()

const emit = defineEmits<{
  selectColor: [id: string, url: string, optionLabel: string]
  selectSize: [type: Exclude<ProductAttributeType, 'color'>, value: string, label: string]
}>()

defineSlots<{
  default: () => void
}>()

const { enableColorAsAttribute, swatches } = useAppConfig().pages.pdp || {}
const { $feature, $t } = useNuxtApp()
const colorId = useId()
const attributeSelectionFormRef = defineModel<InstanceType<typeof BaseFormType> | null>('formRef', { required: false })
const PanelVariantSelection = createPanel('variant-selection')

const colorPickerShape = colorSwatch ?? swatches.variant
const colorPickerSize = colorSize ?? swatches.size
const attributeSelection = defineModel<AttributeSelection>({ required: true })

const selectedProductId = ref()
// When the user hits the PDP for the first time, we store the first selected product to show it as first on the color picker
const firstSelectedProduct = product.id

const variantAttributes = computed(() =>
  isComingSoon
    ? product.attributes.filter((attribute) => attribute.type === 'color')
    : product.attributes
)

const selectedConvertedSizeLabel = computed(() => {
  if (!attributeSelection.value?.size || !product?.variants) return

  const matchedVariant = product.variants.find(
    (variant) => variant.attributes?.size === attributeSelection.value.size
  )

  return matchedVariant?.convertedSizeLabel
})

const showStockStatusLabel = computed(() => $feature.showSizeLevelStockMessagingOnPDP && !hideStockStatusLabel)

const selectNewColor = (id, url, optionLabel) => {
  selectedProductId.value = id
  emit('selectColor', id, url, optionLabel)
}

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.value.color === value : product.id === id

const getIsPending = (value: string, id: string, pending?: boolean) => {
  if (!pending) return

  return enableColorAsAttribute
    ? (attributeSelection.value.color === value && id !== product.id)
    : selectedProductId.value === id
}

const handleSizeSelect = async (attribute) => {
  PanelVariantSelection.open({
    attribute,
  }).then((payload) => {
    if (payload?.handleNextAttribute) handleNextAttribute?.()
  })
}

defineExpose({
  validate: () => attributeSelectionFormRef.value?.validate()
})
</script>
